import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'antd';
import useCredits from '../../hooks/useCredits';
import { chatCreditsNotice, leftLine } from '../../utils/creditsModel';
import { planLanguage, planText } from './planCompareText';
import './sandbox.css';

/**
 * What the chat says about the person's credits (GET /api/v1/me/credits, docs/billing.md): a warning while they run low,
 * and, when they are used up and the organization enforces it, that new work (Chat too) waits until credits are added.
 * The gateway never stops Chat itself, so the panel reads `chatHold` and keeps the chat still while it is true.
 * Nothing shows where the gateway has no billing, billing has not started, or Chat is not part of what is billed.
 * A warning can be dismissed; it comes back when the level changes.
 */
export function useChatCredits() {
  const { me, refresh } = useCredits();
  const notice = chatCreditsNotice(me);
  return { me, notice, hold: Boolean(notice && notice.hold), refresh };
}

export default function CreditsNotice({ me, notice, onRecheck }) {
  const [dismissed, setDismissed] = useState(null);
  const lang = planLanguage();
  const t = planText(lang);
  const level = notice?.level || null;
  useEffect(() => { if (level !== dismissed) { setDismissed(null); } }, [level, dismissed]);
  if (!notice || (dismissed === level && !notice.hold)) { return null; }
  const line = leftLine(me, lang);
  return (
    <div className="sbx-card sbx-plan-notice sbx-credits-notice" role={notice.hold ? 'alert' : 'status'}>
      <div className="sbx-plan-notice-text">
        <strong>{notice.hold ? t('creditsHoldTitle') : t('creditsLowTitle')}</strong>
        <span>
          {notice.level === 'low' ? t('creditsLowText', { line: line || t('creditsTitle') })
            : notice.hold ? t('creditsHoldText') : t('creditsBlockedText')}
        </span>
        {notice.hold && onRecheck && (
          <span className="sbx-plan-notice-links"><Button onClick={onRecheck} size="small">{t('creditsRecheck')}</Button></span>
        )}
      </div>
      {!notice.hold && <button aria-label="Dismiss" className="sbx-plan-notice-close" onClick={() => setDismissed(level)} type="button">×</button>}
    </div>
  );
}

CreditsNotice.propTypes = {
  me: PropTypes.object,
  notice: PropTypes.shape({ level: PropTypes.string, hold: PropTypes.bool }),
  onRecheck: PropTypes.func,
};
